import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, Calendar, Edit, Trash2, Mail, Users, Plus, Download } from 'lucide-react';

const RoadmapsList = () => {
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrestataireFilter, setSelectedPrestataireFilter] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [prestataires, setPrestataires] = useState([]);
  const [selectedPrestataireIds, setSelectedPrestataireIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadPrestataires();
  }, []);

  useEffect(() => {
    loadRoadmaps();
  }, [selectedPrestataireFilter, selectedDateFilter]);

  const loadRoadmaps = async () => {
    try {
      const params = {};
      if (selectedPrestataireFilter) {
        params.prestataire_id = selectedPrestataireFilter;
      }
      if (selectedDateFilter) {
        params.event_date = selectedDateFilter;
      }

      const res = await axios.get(`${API}/roadmaps`, { withCredentials: true, params });
      setRoadmaps(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadPrestataires = async () => {
    try {
      const res = await axios.get(`${API}/prestataires`, { withCredentials: true });
      setPrestataires(res.data);
    } catch (error) {
      console.error('Error loading prestataires:', error);
    }
  };

  const handleDelete = async (roadmapId, eventTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la feuille de route "${eventTitle}" ?`)) return;
    
    try {
      await axios.delete(`${API}/roadmaps/${roadmapId}`, { withCredentials: true });
      toast.success('Feuille de route supprimée');
      loadRoadmaps();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadRoadmap = async (roadmap) => {
    try {
      const response = await axios.get(`${API}/roadmaps/${roadmap.id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename from roadmap data
      const eventDate = roadmap.event_date ? new Date(roadmap.event_date).toISOString().split('T')[0] : '';
      link.download = `Feuille de route - ${roadmap.event_title} - ${eventDate}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading roadmap PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  const openEmailModal = (roadmap) => {
    setSelectedRoadmap(roadmap);
    
    // Extract assigned prestataire IDs from roadmap blocks
    const assignedIds = new Set();
    roadmap.blocks?.forEach(block => {
      block.prestataires?.forEach(p => {
        if (p.prestataire_id) {
          assignedIds.add(p.prestataire_id);
        }
      });
    });
    
    // Pre-select all assigned prestataires
    setSelectedPrestataireIds(Array.from(assignedIds));
    setEmailModalOpen(true);
  };

  const handleSendEmail = async (sendToAll = false) => {
    if (!selectedRoadmap) return;
    
    setSending(true);
    try {
      const payload = sendToAll ? {} : { prestataire_ids: selectedPrestataireIds };
      
      const res = await axios.post(
        `${API}/roadmaps/${selectedRoadmap.id}/send-email`,
        payload,
        { withCredentials: true }
      );
      
      toast.success(res.data.message);
      setEmailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const togglePrestataire = (prestataireId) => {
    setSelectedPrestataireIds(prev => 
      prev.includes(prestataireId)
        ? prev.filter(id => id !== prestataireId)
        : [...prev, prestataireId]
    );
  };

  const getPartnersByDate = (roadmap) => {
    const map = {};
    (roadmap.blocks || []).forEach((block) => {
      const date = block.date || roadmap.event_date || 'Sans date';
      if (!map[date]) {
        map[date] = new Set();
      }

      (block.prestataires || []).forEach((p) => {
        const name = (p.name || '').trim();
        if (name) {
          map[date].add(name);
        }
      });
    });

    return Object.entries(map).map(([date, namesSet]) => ({
      date,
      names: Array.from(namesSet),
    }));
  };

  // Get assigned prestataires for the selected roadmap
  const getAssignedPrestataires = () => {
    if (!selectedRoadmap) return [];
    
    const assignedIds = new Set();
    selectedRoadmap.blocks?.forEach(block => {
      block.prestataires?.forEach(p => {
        if (p.prestataire_id) {
          assignedIds.add(p.prestataire_id);
        }
      });
    });
    
    return prestataires.filter(p => assignedIds.has(p.id));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800">Feuilles de route</h1>
          <p className="text-sm sm:text-base text-stone-600">Gérez les feuilles de route des événements</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <input
            type="date"
            className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm"
            value={selectedDateFilter}
            onChange={(e) => setSelectedDateFilter(e.target.value)}
          />
          <select
            className="h-10 rounded-md border border-stone-300 bg-white px-3 text-sm"
            value={selectedPrestataireFilter}
            onChange={(e) => setSelectedPrestataireFilter(e.target.value)}
          >
            <option value="">Tous les prestataires</option>
            {prestataires.map((p) => (
              <option key={p.id} value={p.id}>
                {p.prenom} {p.nom}
              </option>
            ))}
          </select>
          <Link to="/backoffice/roadmaps/new">
            <Button className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Créer une feuille de route
            </Button>
          </Link>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Événement</TableHead>
                <TableHead>Date(s)</TableHead>
                  <TableHead>Partenaires par date</TableHead>
                <TableHead>Blocs</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Chargement...</TableCell></TableRow>
              ) : roadmaps.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-stone-500">Aucune feuille de route. Créez-en une depuis un devis.</TableCell></TableRow>
              ) : (
                roadmaps.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.event_title}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-stone-400" />
                        {r.event_date}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {getPartnersByDate(r).length === 0 ? (
                          <span className="text-xs text-stone-500">Aucun partenaire</span>
                        ) : (
                          getPartnersByDate(r).map((entry) => (
                            <div key={entry.date} className="text-xs">
                              <span className="font-medium text-stone-700">{entry.date}:</span>{' '}
                              <span className="text-stone-600">
                                {entry.names.length > 0 ? entry.names.join(', ') : 'Aucun partenaire'}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{r.blocks?.length || 0} bloc(s)</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" asChild title="Voir">
                          <Link to={`/roadmap/${r.id}`} target="_blank">
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDownloadRoadmap(r)}
                          title="Télécharger la feuille de route (PDF)"
                          className="text-amber-600 hover:text-amber-700"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" asChild title="Modifier">
                          <Link to={`/backoffice/roadmaps/${r.id}/edit`}>
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEmailModal(r)}
                          title="Envoyer par email"
                        >
                          <Mail className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(r.id, r.event_title)}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer la feuille de route</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📅 {selectedRoadmap?.event_title}</strong>
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {selectedRoadmap?.event_date}
              </p>
            </div>

            {/* Quick action: Send to all */}
            <Button 
              onClick={() => handleSendEmail(true)}
              disabled={sending}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              <Users className="h-4 w-4 mr-2" />
              {sending ? 'Envoi en cours...' : 'Envoyer à tous les prestataires assignés'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-stone-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-stone-500">ou sélectionner</span>
              </div>
            </div>

            {/* Manual selection */}
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
              <p className="text-sm font-medium text-stone-700 mb-2">Prestataires assignés :</p>
              {getAssignedPrestataires().length === 0 ? (
                <p className="text-sm text-stone-500 italic">Aucun prestataire assigné</p>
              ) : (
                getAssignedPrestataires().map(presta => (
                  <div key={presta.id} className="flex items-center space-x-2 py-2 border-b last:border-0">
                    <Checkbox
                      id={`presta-${presta.id}`}
                      checked={selectedPrestataireIds.includes(presta.id)}
                      onCheckedChange={() => togglePrestataire(presta.id)}
                    />
                    <Label htmlFor={`presta-${presta.id}`} className="cursor-pointer flex-1 text-sm">
                      {presta.prenom} {presta.nom}
                      {presta.email ? (
                        <span className="text-xs text-stone-500 block">{presta.email}</span>
                      ) : (
                        <span className="text-xs text-rose-500 block">⚠️ Pas d&apos;email</span>
                      )}
                    </Label>
                  </div>
                ))
              )}
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEmailModalOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={() => handleSendEmail(false)}
                disabled={sending || selectedPrestataireIds.length === 0}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sending ? 'Envoi...' : `Envoyer (${selectedPrestataireIds.length})`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoadmapsList;
